import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Spin, Switch, Tooltip } from 'antd';
import { DeleteOutlined, MailOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import LedgerEmailOtpDialog from './LedgerEmailOtpDialog';
import ConfirmDialog from './ConfirmDialog';
import {
  fetchLedgerEmails,
  removeLedgerEmail,
  sendLedgerEmailOtp,
  setLedgerEmailStatus,
  type LedgerEmailEntry,
  type LedgerEmailOtpResponse,
} from './ledgerEmailsApi';

interface AddEmailValues {
  email: string;
}

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const LAST_ACTIVE_HINT =
  'At least one email must stay active for the ledger OTP';

// Rules for the list:
//  - an added email can be switched Active / Deactive and removed, never edited
//  - the account email can only be switched Active / Deactive (no Edit, no Remove)
//  - at least one email always stays Active for the ledger OTP
export default function LedgerEmailsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [addForm] = Form.useForm<AddEmailValues>();

  const [emails, setEmails] = useState<LedgerEmailEntry[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  // add: the OTP was sent to `email`; the dialog verifies it before anything is saved
  // the email waiting for the "Remove this email?" confirmation popup
  const [removing, setRemoving] = useState<LedgerEmailEntry | null>(null);
  const [otpDialog, setOtpDialog] = useState<{
    email: string;
    sent: LedgerEmailOtpResponse;
  } | null>(null);

  const activeCount = emails.filter(
    (entry) => entry.status === 'active'
  ).length;
  // the only active email cannot be switched off
  const isLastActive = (entry: LedgerEmailEntry): boolean =>
    entry.status === 'active' && activeCount === 1;
  // the last active email cannot be removed either
  const cannotRemove = (entry: LedgerEmailEntry): boolean =>
    isLastActive(entry);

  // Returns true when the error was handled (401 -> back to sign-in)
  const handleAuthError = (status: number | undefined): boolean => {
    if (status === 401) {
      window.localStorage.clear();
      navigate('/sign-in');
      return true;
    }
    return false;
  };

  const messageOf = (error: any): string => {
    const message = error?.response?.data?.message;
    return typeof message === 'string' ? message : GENERIC_MESSAGE;
  };

  const loadEmails = useCallback(async () => {
    setIsBusy(true);
    try {
      const data = await fetchLedgerEmails();
      setEmails(data.emails ?? []);
    } catch (error: any) {
      const status: number | undefined = error?.response?.status;
      if (status === 401) {
        window.localStorage.clear();
        navigate('/sign-in');
      } else {
        const message = error?.response?.data?.message;
        dispatch(
          showErrorMessage(
            typeof message === 'string' ? message : GENERIC_MESSAGE
          )
        );
      }
    } finally {
      setIsBusy(false);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    void loadEmails();
  }, [loadEmails]);

  const alreadyListed = (email: string): boolean =>
    emails.some((entry) => entry.email.toLowerCase() === email.toLowerCase());

  // Add: send the OTP to the new address; it is only saved after the code is verified
  const onAdd = async (values: AddEmailValues) => {
    if (isBusy) return;
    const email = values.email.trim();
    if (alreadyListed(email)) {
      addForm.setFields([
        { name: 'email', errors: ['This email is already in the list'] },
      ]);
      return;
    }
    setIsBusy(true);
    try {
      const sent = await sendLedgerEmailOtp({ email });
      setOtpDialog({ email, sent });
    } catch (error: any) {
      const status: number | undefined = error?.response?.status;
      const data = error?.response?.data;
      const keys: string[] | undefined = data?.validation?.body?.keys;
      if (handleAuthError(status)) return;
      if (data?.field === 'email') {
        // e.g. "This email is already in the list", "Unable to add this email..."
        addForm.setFields([{ name: 'email', errors: [messageOf(error)] }]);
      } else if (Array.isArray(keys) && keys.includes('email')) {
        addForm.setFields([
          {
            name: 'email',
            errors: [
              data?.validation?.body?.message ?? 'Enter a valid email address',
            ],
          },
        ]);
      } else {
        // 429 "Please wait N seconds...", 500 "Failed to send OTP email..."
        dispatch(showErrorMessage(messageOf(error)));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const onToggle = async (entry: LedgerEmailEntry, turnOn: boolean) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      const res = await setLedgerEmailStatus(
        entry.email,
        turnOn ? 'active' : 'deactive'
      );
      setEmails(res.emails);
      dispatch(showSuccessMessage(res.message ?? 'Status updated'));
    } catch (error: any) {
      if (handleAuthError(error?.response?.status as number | undefined)) {
        return;
      }
      // e.g. "At least one email must stay active for the ledger OTP"
      dispatch(showErrorMessage(messageOf(error)));
    } finally {
      setIsBusy(false);
    }
  };

  const onRemove = async (entry: LedgerEmailEntry) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      const res = await removeLedgerEmail(entry.email);
      setEmails(res.emails);
      dispatch(showSuccessMessage(res.message ?? 'Email removed'));
    } catch (error: any) {
      if (handleAuthError(error?.response?.status as number | undefined)) {
        return;
      }
      dispatch(showErrorMessage(messageOf(error)));
      if (error?.response?.status === 404) void loadEmails();
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="settings__card">
      <Spin spinning={isBusy} fullscreen />
      <p className="settings__title">Ledger Emails</p>
      <p className="settings__subtitle">
        The OTP for downloading the ledger is sent to every email that is
        active. A new email is only added after you verify it with an OTP.
      </p>

      <Form
        form={addForm}
        layout="vertical"
        requiredMark={(label, { required }) => (
          <>
            {label}
            {required && <span className="settings__req">*</span>}
          </>
        )}
        onFinish={onAdd}
      >
        <Form.Item
          label="Add email"
          name="email"
          rules={[
            { required: true, message: 'Please enter an email address' },
            { type: 'email', message: 'Enter a valid email address' },
          ]}
        >
          <Input
            className="settings__input"
            placeholder="name@company.com"
            prefix={<MailOutlined />}
            onPressEnter={() => {
              addForm.submit();
            }}
          />
        </Form.Item>
        <div className="settings__actions">
          <Button
            htmlType="submit"
            className="settings__button settings__button--primary"
          >
            Add email
          </Button>
        </div>
      </Form>

      <p className="ledger-emails__heading">Ledger emails ({emails.length})</p>
      {emails.length === 0 ? (
        <p className="ledger-emails__empty">No ledger emails added yet.</p>
      ) : (
        <ul className="ledger-emails__list">
          {emails.map((entry) => (
            <li key={entry.email} className="ledger-emails__item">
              <span className="ledger-emails__address">
                <MailOutlined />
                <Tooltip title={entry.email}>
                  <span className="ledger-emails__email-text">
                    {entry.email}
                  </span>
                </Tooltip>
              </span>

              <div className="ledger-emails__status">
                <Tooltip title={isLastActive(entry) ? LAST_ACTIVE_HINT : ''}>
                  <span className="status-toggle">
                    <Switch
                      checked={entry.status === 'active'}
                      disabled={isLastActive(entry)}
                      onChange={(checked) => {
                        void onToggle(entry, checked);
                      }}
                    />
                    <span
                      className={`status-toggle__text${
                        entry.status === 'active' ? ' is-active' : ''
                      }`}
                    >
                      {entry.status === 'active' ? 'Active' : 'Deactive'}
                    </span>
                  </span>
                </Tooltip>
              </div>

              <div className="ledger-emails__action">
                {entry.isAccountEmail ? (
                  // the account email: just the badge (no Edit, no Remove)
                  <Tooltip title="Account email: the email your organization signed up with. It stays in the list and can't be edited or removed here.">
                    <span className="ledger-emails__tag">Account email</span>
                  </Tooltip>
                ) : // an added email: Remove only (no Edit), except the last active email
                cannotRemove(entry) ? (
                  <Tooltip title={LAST_ACTIVE_HINT}>
                    <span className="settings__disabled-wrap">
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        className="ledger-emails__btn"
                        disabled
                      >
                        Remove
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    className="ledger-emails__btn"
                    onClick={() => {
                      setRemoving(entry);
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Remove email"
        message={
          <>
            Remove <strong>{removing?.email}</strong>? It will no longer receive
            the ledger OTP.
          </>
        }
        confirmText="Remove"
        onCancel={() => {
          setRemoving(null);
        }}
        onConfirm={() => {
          const entry = removing;
          setRemoving(null);
          if (entry !== null) void onRemove(entry);
        }}
      />

      <LedgerEmailOtpDialog
        open={otpDialog !== null}
        email={otpDialog?.email ?? ''}
        sent={otpDialog?.sent ?? null}
        onClose={(message) => {
          const dialog = otpDialog;
          setOtpDialog(null);
          if (message !== undefined && dialog !== null) {
            // the address cannot be used: show why under the input it came from
            addForm.setFields([{ name: 'email', errors: [message] }]);
          }
        }}
        onDone={(updated) => {
          setEmails(updated);
          setOtpDialog(null);
          addForm.resetFields();
        }}
      />
    </div>
  );
}
